import { X } from 'lucide-react';
import { useState, type ReactNode } from 'react';

export type ProfileBannerTone = 'slate' | 'blue' | 'emerald' | 'amber' | 'red';

const TONES: Record<ProfileBannerTone, string> = {
  slate: 'bg-slate-50 text-slate-700 ring-slate-200',
  blue: 'bg-blue-50 text-blue-800 ring-blue-200',
  emerald: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  amber: 'bg-amber-50 text-amber-800 ring-amber-200',
  red: 'bg-red-50 text-red-800 ring-red-200',
};

/**
 * The banner at the top of a profile's content — the record's state in one line, with the
 * action that changes it on the right.
 *
 * **Every profile page has one.** It states what the record currently is, for every state
 * and not only the bad ones: a banner that appears only when something is wrong teaches the
 * reader to skip the region, and then it is missed on the day it matters.
 *
 * It is the home of the **record-level lifecycle action** — Disable, Enable. Those
 * act on the whole record rather than on one card's fields, so they belong here rather than
 * in a card header or a toolbar of abbreviations.
 *
 * Dismissal is per visit and deliberately not persisted: the state is still true after the
 * banner is closed, so it must come back on the next load rather than being permanently
 * silenced. The identity block's status pill is the always-on indicator that survives it.
 */
export function ProfileBanner({
  tone = 'slate',
  icon,
  message,
  action,
  dismissible = true,
  id,
}: {
  tone?: ProfileBannerTone;
  icon?: ReactNode;
  /** The left side. One sentence about what the record is right now. */
  message: ReactNode;
  /** The right side. Optional — a state can be worth stating with nothing to do about it. */
  action?: ReactNode;
  dismissible?: boolean;
  id?: string;
}) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) {
    return null;
  }

  return (
    <div
      id={id}
      role="status"
      className={`flex min-w-0 items-center gap-3 rounded-xl px-4 py-3 ring-1 ${TONES[tone]}`}
    >
      {icon ? (
        <span className="shrink-0" aria-hidden="true">
          {icon}
        </span>
      ) : null}

      <p className="min-w-0 flex-1 text-sm">{message}</p>

      {action ? <div className="shrink-0">{action}</div> : null}

      {dismissible ? (
        <button
          type="button"
          id={id ? `${id}-dismiss` : undefined}
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="shrink-0 rounded-md p-1 opacity-60 transition hover:bg-white/60 hover:opacity-100"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
